import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "../AuthProvider";

const fmtB = (n) => {
  const x = Number(n || 0);
  if (x >= 1e12) return (x / 1e12).toFixed(1) + "T";
  if (x >= 1e9) return (x / 1e9).toFixed(1) + "B";
  if (x >= 1e6) return (x / 1e6).toFixed(1) + "M";
  return x.toLocaleString("id-ID");
};

const fmtInt = (n) => {
  if (n == null) return "-";
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return Math.round(x).toLocaleString("id-ID");
};


// Average: bulatkan ke atas (ceil), TANPA bagi 1000
const fmtAvgCeil = (n) => {
  if (n == null) return "-";
  const x = Number(n);
  if (!Number.isFinite(x)) return "-";
  return String(Math.ceil(x));
};


const toneOfFlow = (v) => {
  const s = String(v || "").toUpperCase();
  if (s.includes("ACC")) return "green";
  if (s.includes("DIST")) return "red";
  return "gray";
};

const labelOfFlow = (v) => {
  const s = String(v || "").toUpperCase();
  if (s.includes("ACC")) return "Accumulation";
  if (s.includes("DIST")) return "Distribution";
  return "Neutral";
};

const pillTone = (tone) => {
  if (tone === "green") return "border-emerald-500/30 bg-emerald-500/10 text-emerald-200";
  if (tone === "red") return "border-rose-500/30 bg-rose-500/10 text-rose-200";
  return "border-gray-500/30 bg-gray-500/10 text-gray-200";
};

const dotTone = (tone) => {
  if (tone === "green") return "bg-emerald-400 shadow-[0_0_0_2px_rgba(16,185,129,.15)]";
  if (tone === "red") return "bg-rose-400 shadow-[0_0_0_2px_rgba(244,63,94,.15)]";
  return "bg-gray-400 shadow-[0_0_0_2px_rgba(156,163,175,.15)]";
};

function MiniFlowBadges({ foreignFlow, brokerAllFlow }) {
  // tampilkan hanya kalau ada salah satunya
  if (!foreignFlow && !brokerAllFlow) return null;

  const fTone = toneOfFlow(foreignFlow);
  const aTone = toneOfFlow(brokerAllFlow);

  return (
    <div className="mb-2 flex flex-wrap gap-2 text-[12px] px-4 pt-3">
      <span className={`inline-flex items-center gap-2 rounded-full border px-2 py-1 ${pillTone(fTone)}`}>
        <span className={`h-2 w-2 rounded-full ${dotTone(fTone)}`} />
        <span className="text-gray-200/90">Foreign Flow</span>
        <span className="font-semibold">{labelOfFlow(foreignFlow)}</span>
      </span>

      <span className={`inline-flex items-center gap-2 rounded-full border px-2 py-1 ${pillTone(aTone)}`}>
        <span className={`h-2 w-2 rounded-full ${dotTone(aTone)}`} />
        <span className="text-gray-200/90">Broker ALL</span>
        <span className="font-semibold">{labelOfFlow(brokerAllFlow)}</span>
      </span>
    </div>
  );
}

function SignalForm10D({ signalForm }) {
  if (!signalForm) return null;

  const form = String(signalForm.form || "").trim();
  const dates = Array.isArray(signalForm.dates) ? signalForm.dates : [];
  const chars = form ? form.split("") : [];

  const bwStreak = Number(signalForm.bw_streak ?? 0);
  const bwCount = Number(signalForm.bw_count ?? 0);
  const bw5 = Boolean(signalForm.bw_5d_ok);
  const bw10 = Boolean(signalForm.bw_10d_ok);

  const tone = (ch) => {
    switch ((ch || "").toUpperCase()) {
      case "B":
        return "bg-emerald-500/15 border-emerald-500/30 text-emerald-200";
      case "W":
        return "bg-amber-500/15 border-amber-500/30 text-amber-200";
      case "A":
        return "bg-rose-500/15 border-rose-500/30 text-rose-200";
      default:
        return "bg-gray-500/10 border-gray-500/25 text-gray-200";
    }
  };

  const label = (ch) => {
    switch ((ch || "").toUpperCase()) {
      case "B":
        return "BUY";
      case "W":
        return "WATCH";
      case "A":
        return "AVOID";
      default:
        return "NEUTRAL";
    }
  };

  if (!chars.length) return null;

  return (
    <div className="px-4 pb-2">
      <div className="rounded-xl border border-gray-800 bg-gray-950/30 p-3">
        <div className="flex items-center justify-between gap-2">
          <div className="text-xs font-semibold text-gray-100">
            Signal Form 10D{" "}
            {signalForm.engine ? (
              <span className="ml-2 text-[11px] font-medium text-gray-400">
                ({signalForm.engine})
              </span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            {bw5 ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-200">
                🔥 5D BW
              </span>
            ) : null}
            {bw10 ? (
              <span className="text-[11px] px-2 py-0.5 rounded-full border border-indigo-500/30 bg-indigo-500/10 text-indigo-200">
                🏆 10D BW
              </span>
            ) : null}
          </div>
        </div>

        {/* Chips: urutan terlama -> terbaru (kiri ke kanan) */}
        <div className="mt-2 -mx-1 px-1 flex gap-1.5 flex-nowrap overflow-x-auto">
          {chars.map((ch, idx) => {
            const dt = dates[idx] || "";
            const title = dt ? `${dt} • ${label(ch)}` : label(ch);
            return (
              <span
                key={`${idx}-${ch}`}
                title={title}
                className={`inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 rounded-lg border text-[11px] md:text-xs font-bold ${tone(ch)}`}
              >
                {String(ch).toUpperCase()}
              </span>
            );
          })}
        </div>

        {/* Summary */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-2">
            <div className="text-[11px] text-gray-400">BW Streak</div>
            <div className="text-sm font-semibold text-gray-100">{bwStreak}</div>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-2">
            <div className="text-[11px] text-gray-400">BW Count</div>
            <div className="text-sm font-semibold text-gray-100">{bwCount}/10</div>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-950/40 p-2">
            <div className="text-[11px] text-gray-400">Indikasi</div>
            <div className="text-sm font-semibold text-gray-100">
              {bwStreak >= 5 ? "Strong" : bwStreak >= 3 ? "Improving" : "Mixed"}
            </div>
          </div>
        </div>

        <div className="mt-2 text-[11px] text-gray-500">
          Urutan: <span className="text-gray-300">terlama → terbaru</span>.
        </div>
      </div>
    </div>
  );
}

export default function BrokerSummaryCard({ code, tradeDate }) {
  const { token } = useAuth() || {};
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!code) return;

    const run = async () => {
      setLoading(true);
      setErr(null);
      try {
        const headers = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const qs = new URLSearchParams();
        if (tradeDate) qs.set("trade_date", tradeDate);
        qs.set("fallback", "1");
        qs.set("limit", "10");

        const res = await fetch(`/api/stocks/${code}/broker-summary?${qs.toString()}`, { headers });
        const json = await res.json();
        if (!res.ok) throw new Error(json.detail || "Failed to load broker summary");
        setData(json);
      } catch (e) {
        setErr(e.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [code, tradeDate, token]);

  const buyers = useMemo(() => (Array.isArray(data?.buyers) ? data.buyers : []), [data]);
  const sellers = useMemo(() => (Array.isArray(data?.sellers) ? data.sellers : []), [data]);

  if (loading) {
    return (
      <div className="mt-3 p-3 rounded-lg border border-gray-700 bg-gray-900 text-xs text-gray-400">
        Loading broker summary…
      </div>
    );
  }

  if (err) {
    return (
      <div className="mt-3 p-3 rounded-lg border border-red-700 bg-red-900/20 text-xs text-red-300">
        Error: {err}
      </div>
    );
  }

  if (!data || data.status !== "OK") {
    return (
      <div className="mt-3 p-3 rounded-lg border border-gray-700 bg-gray-900 text-xs text-gray-400">
        Broker summary belum tersedia.
      </div>
    );
  }

  const actionColor =
    data.action === "BIG_ACC"
      ? "text-emerald-300"
      : data.action === "BIG_DIST"
      ? "text-red-300"
      : "text-gray-300";

  // Optional: kalau backend kamu sudah kirim field ini, badge akan otomatis tampil.
  // Kalau belum ada, aman (tidak tampil).
  const foreignFlow = data.foreign_flow || data.foreign_flow_signal || null;
  const brokerAllFlow =
    data.broker_all_flow || data.broker_all_signal || data.action || null; // fallback: pakai action ALL

  return (
    <div className="mt-3 rounded-xl border border-gray-700 bg-gray-900">
      
      <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
        <div className="text-sm font-semibold text-white">Aksi Broker (NET ALL)</div>
        <div className="text-[11px] text-gray-400">
          Date: <span className="text-gray-200 font-mono">{data.broksum_date_used}</span>
          {data.broksum_date_used !== data.trade_date ? (
            <span className="ml-2 text-amber-300">(fallback)</span>
          ) : null}
        </div>
      </div>

      {/* Mini badges */}
      <MiniFlowBadges foreignFlow={foreignFlow} brokerAllFlow={brokerAllFlow} />

      {/* Signal Form 10D */}
      <SignalForm10D signalForm={data?.signal_form} />

      <div className="px-4 py-3 text-xs text-gray-300 flex flex-wrap gap-4">
        <div>
          <div className="text-gray-400">Action</div>
          <div className={`font-semibold ${actionColor}`}>
            {data.action === "BIG_ACC" ? "Big Acc" : data.action === "BIG_DIST" ? "Big Dist" : "Neutral"}
          </div>
        </div>
        <div>
          <div className="text-gray-400">Net</div>
          <div className="font-semibold">{fmtB(data.net_value)}</div>
        </div>
        <div>
          <div className="text-gray-400">Buy</div>
          <div className="font-semibold text-emerald-300">{fmtB(data.buy_total)}</div>
        </div>
        <div>
          <div className="text-gray-400">Sell</div>
          <div className="font-semibold text-red-300">{fmtB(data.sell_total)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-0 border-t border-gray-700">
        {/* Buyers */}
        <div className="p-3">
          <div className="text-xs font-semibold text-emerald-300 mb-2">Top Buyers</div>

          {/* Header */}
          <div className="grid grid-cols-4 gap-2 px-1 py-1 text-[11px] text-gray-400">
            <div className="font-semibold">BC</div>
            <div className="text-right font-semibold">Value</div>
            <div className="text-right font-semibold">Lot</div>
            <div className="text-right font-semibold">Average</div>
          </div>

          <div className="space-y-1">
            {buyers.map((r) => (
              <div key={r.broker} className="grid grid-cols-4 gap-2 text-[11px] text-gray-200">
                <div className="font-mono">{r.broker}</div>
                <div className="text-right text-emerald-300">{fmtB(r.val)}</div>
                <div className="text-right text-gray-300">{fmtInt(r.lot)}</div>
                <div className="text-right text-gray-300">{fmtAvgCeil(r.avg)}</div>
              </div>
            ))}
            {buyers.length === 0 ? (
              <div className="text-[11px] text-gray-500 px-1 py-2">Tidak ada data.</div>
            ) : null}
          </div>
        </div>

        {/* Sellers */}
        <div className="p-3 border-l border-gray-700">
          <div className="text-xs font-semibold text-red-300 mb-2">Top Sellers</div>

          {/* Header */}
          <div className="grid grid-cols-4 gap-2 px-1 py-1 text-[11px] text-gray-400">
            <div className="font-semibold">BC</div>
            <div className="text-right font-semibold">Value</div>
            <div className="text-right font-semibold">Lot</div>
            <div className="text-right font-semibold">Average</div>
          </div>

          <div className="space-y-1">
            {sellers.map((r) => (
              <div key={r.broker} className="grid grid-cols-4 gap-2 text-[11px] text-gray-200">
                <div className="font-mono">{r.broker}</div>
                <div className="text-right text-red-300">{fmtB(r.val)}</div>
                <div className="text-right text-gray-300">{fmtInt(r.lot)}</div>
                <div className="text-right text-gray-300">{fmtAvgCeil(r.avg)}</div>
              </div>
            ))}
            {sellers.length === 0 ? (
              <div className="text-[11px] text-gray-500 px-1 py-2">Tidak ada data.</div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
